import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

/**
 * Lazy-loaded iframe — defers loading until the iframe is within the viewport.
 * Improves initial page load (Lighthouse score) by deferring 3rd-party scripts.
 */
export default function LazyIframe({
  src,
  title,
  height = 900,
  className = "",
  testId,
  rootMargin = "200px",
  ...rest
}) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const el = wrapRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin]);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ minHeight: height }}
      data-testid={testId}
    >
      {visible ? (
        <iframe
          title={title}
          src={src}
          width="100%"
          height={height}
          frameBorder="0"
          className="block w-full"
          loading="lazy"
          {...rest}
        />
      ) : (
        <div
          className="w-full flex items-center justify-center bg-soft text-navy/50 text-sm gap-2"
          style={{ minHeight: height }}
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
    </div>
  );
}
